import React, { useCallback } from 'react';
import { DragDropContext, Droppable } from 'react-beautiful-dnd';
import { PropTypes } from 'prop-types';

import {
    editingQuestionSet,
    editingStepIdSet,
    newStepSet,
    newStepUpdated,
} from 'reducers/customTunnel';
import { useDispatch, useSelector } from 'react-redux';
import { customTunnelSelectors } from 'reducers';
import {
    useUpdateAnswerMutation,
    useUpdateCustomTunnelStepMutation,
    useUpdateQuestionMutation,
} from 'api/main';
import { texts } from 'utils/languages/fr';

import AddCustomStep from './AddCustomStep';
import TunnelStep from './TunnelStep';
import { ActionButton, Loader } from 'components';
import { Box } from '@mui/material';

const CustomSteps = ({
    ppTunnelPhaseId,
    pmTunnelPhaseId,
    ppStepOrder,
    pmStepOrder,
    customSteps,
}) => {
    const dispatch = useDispatch();
    const [updateTunnelStep, { isLoading: stepOrderLoading }] = useUpdateCustomTunnelStepMutation();
    const [updateQuestion] = useUpdateQuestionMutation({ fixedCacheKey: 'question-order' });
    const [updateAnswer] = useUpdateAnswerMutation({ fixedCacheKey: 'answerOrder' });

    const { ppId: editingStepId } = useSelector(customTunnelSelectors.getEditingStepId);
    const newStep = useSelector(customTunnelSelectors.getNewStep);
    const editingQuestion = useSelector(customTunnelSelectors.getEditingQuestion);

    const handleNewStep = useCallback(() => {
        dispatch(newStepSet({ title: '', subtitle: '', questions: [] }));
        dispatch(editingStepIdSet({ ppId: 0, pmId: 0 })); // id 0 means new entry
    }, [dispatch]);

    const handleDrop = useCallback(
        result => {
            if (!result.destination || result.destination.index === result.source.index) {
                // outside of container area or position has not changed
                return;
            }
            let idParts = result.draggableId.split('-');
            if (result.type === 'step') {
                // update step position in the DB
                updateTunnelStep({
                    ppId: idParts[0],
                    pmId: idParts[1],
                    ppStepOrder: ppStepOrder - customSteps.length + result.destination.index + 1,
                    pmStepOrder: pmStepOrder - customSteps.length + result.destination.index + 1,
                });
            } else if (result.type.includes('question')) {
                if (editingStepId === 0) {
                    // only update positions in the new step form
                    let newQuestions = [...(newStep.questions ?? [])];
                    const movedQuestion = newQuestions.splice(result.source.index, 1)[0];
                    newQuestions.splice(result.destination.index, 0, movedQuestion);
                    dispatch(newStepUpdated({ key: 'questions', value: newQuestions }));
                } else if (idParts?.[0]) {
                    updateQuestion({
                        ppQuestionId: idParts[0],
                        pmQuestionId: idParts[1],
                        order: result.destination.index + 1,
                    });
                }
            } else if (result.type.includes('answer')) {
                if (editingStepId === 0 || !idParts[0]) {
                    // only update positions in the form data from store
                    let newAnswers = [...editingQuestion.answers];
                    const movedAnswer = newAnswers.splice(result.source.index, 1)[0];
                    newAnswers.splice(result.destination.index, 0, movedAnswer);
                    dispatch(editingQuestionSet({ key: 'answers', value: newAnswers }));
                } else {
                    // update answer with new position
                    updateAnswer({
                        ppId: idParts[0],
                        pmId: idParts[1],
                        order: result.destination.index + 1,
                    });
                }
            }
        },
        [
            updateTunnelStep,
            ppStepOrder,
            customSteps,
            pmStepOrder,
            editingStepId,
            newStep,
            editingQuestion,
            dispatch,
            updateQuestion,
            updateAnswer,
        ]
    );

    return (
        <Box sx={{ gridColumn: '1 / span 2', padding: '8px 16px' }}>
            <Box textAlign="center" paddingY={1}>
                <ActionButton
                    disabled={!!editingStepId}
                    variant="contained"
                    onClick={handleNewStep}
                >
                    {texts.ADD_STEP}
                </ActionButton>
            </Box>
            <DragDropContext onDragEnd={handleDrop}>
                <Droppable droppableId="droppable-steps" type="step">
                    {(provided, snapshot) => (
                        <Box
                            {...provided.droppableProps}
                            sx={{ opacity: snapshot.isDraggingOver ? '.85' : 1 }}
                            ref={provided.innerRef}
                            paddingY={1}
                        >
                            <Loader loading={stepOrderLoading}>
                                {customSteps.map((ts, index) => (
                                    <TunnelStep key={ts.id} tunnelStep={ts} index={index} />
                                ))}
                                {provided.placeholder}
                            </Loader>
                        </Box>
                    )}
                </Droppable>
                {editingStepId === 0 && (
                    <AddCustomStep
                        ppTunnelPhaseId={ppTunnelPhaseId}
                        pmTunnelPhaseId={pmTunnelPhaseId}
                        ppStepOrder={ppStepOrder}
                        pmStepOrder={pmStepOrder}
                    />
                )}
            </DragDropContext>
        </Box>
    );
};

CustomSteps.propTypes = {
    ppTunnelPhaseId: PropTypes.number,
    pmTunnelPhaseId: PropTypes.number,
    ppStepOrder: PropTypes.number,
    pmStepOrder: PropTypes.number,
    customSteps: PropTypes.array,
};

export default CustomSteps;
